import { useState } from "react";

import moment from "moment";

import { Button, Input, Select } from "@alpacahq/ui/old";

import type { ChartingLibraryFeatureset } from "~/chart";
import { TRADINGVIEW_FEATURES } from "~/v2/components/TradingView/constants";
import { TVChartContainer } from "~/v2/components/TradingView/TradingViewChartContainer";
import { useLocalStorage } from "~/v2/hooks/useLocalStorage";
import { cn } from "~/v2/utils/cn";
export const TradingViewConfig = () => {
  const [tradingViewConfigOpen, setTradingViewConfigOpen] =
    useLocalStorage<boolean>("devtools-tradingview-config-open");

  // TradingView Configuration State
  const [tvSymbol, setTvSymbol] = useLocalStorage<string>("devtools-tv-symbol");
  const [tvTheme, setTvTheme] = useLocalStorage<"light" | "dark">(
    "devtools-tv-theme",
  );
  const [tvInterval, setTvInterval] = useLocalStorage<string>(
    "devtools-tv-interval",
  );
  const [tvSeriesStyle, setTvSeriesStyle] = useLocalStorage<number>(
    "devtools-tv-series-style",
  );
  const [tvEnabledFeatures, setTvEnabledFeatures] = useLocalStorage<
    ChartingLibraryFeatureset[]
  >("devtools-tv-enabled-features");
  const [tvDisabledFeatures, setTvDisabledFeatures] = useLocalStorage<
    ChartingLibraryFeatureset[]
  >("devtools-tv-disabled-features");
  const [tvHeight, setTvHeight] = useLocalStorage<number>("devtools-tv-height");
  const [featureSearchQuery, setFeatureSearchQuery] = useState<string>("");

  // TradingView helper functions with null-safe defaults
  const enabledFeatures = tvEnabledFeatures ?? [];
  const disabledFeatures = tvDisabledFeatures ?? [];
  const currentSymbol = tvSymbol ?? "AAPL";
  const currentTheme = tvTheme ?? "light";
  const currentInterval = tvInterval ?? "15";
  const currentSeriesStyle = tvSeriesStyle ?? 1;
  const currentHeight = tvHeight ?? 500;

  const getFeatureState = (
    featureName: ChartingLibraryFeatureset,
  ): "default" | "enabled" | "disabled" => {
    if (enabledFeatures.includes(featureName)) {
      return "enabled";
    }
    if (disabledFeatures.includes(featureName)) {
      return "disabled";
    }
    return "default";
  };

  const toggleFeature = (featureName: ChartingLibraryFeatureset) => {
    const currentState = getFeatureState(featureName);

    if (currentState === "default") {
      // Move to enabled
      setTvEnabledFeatures([...enabledFeatures, featureName]);
    } else if (currentState === "enabled") {
      // Move to disabled
      setTvEnabledFeatures(enabledFeatures.filter((f) => f !== featureName));
      setTvDisabledFeatures([...disabledFeatures, featureName]);
    } else {
      // Move back to default
      setTvDisabledFeatures(disabledFeatures.filter((f) => f !== featureName));
    }
  };

  const resetTradingViewConfig = () => {
    setTvSymbol("AAPL");
    setTvTheme("light");
    setTvInterval("15");
    setTvSeriesStyle(1);
    setTvHeight(500);
    setTvEnabledFeatures([]);
    setTvDisabledFeatures([]);
    setFeatureSearchQuery("");
  };

  // Filter features based on search query
  const filteredFeatures = TRADINGVIEW_FEATURES.filter(
    (feature) =>
      feature.name.toLowerCase().includes(featureSearchQuery.toLowerCase()) ||
      feature.description
        .toLowerCase()
        .includes(featureSearchQuery.toLowerCase()),
  );

  return (
    <div className="overflow-hidden rounded border border-gray-300">
      <button
        type="button"
        onClick={() =>
          setTradingViewConfigOpen(!(tradingViewConfigOpen ?? false))
        }
        className="flex w-full items-center justify-between bg-gray-50 px-4 py-3 transition-colors hover:bg-gray-100"
      >
        <h2 className="m-0 font-semibold text-lg">TradingView Configuration</h2>
        <span className="text-gray-500">
          {(tradingViewConfigOpen ?? false) ? "−" : "+"}
        </span>
      </button>
      {(tradingViewConfigOpen ?? false) && (
        <div className="flex flex-col gap-4 p-4">
          {/* Common Options */}
          <div className="rounded border border-gray-200 p-4">
            <h3 className="mb-3 font-semibold text-base">Common Options</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-1 block font-medium text-sm">Symbol</label>
                <Input
                  value={currentSymbol}
                  onChange={(e) => setTvSymbol(e.target.value)}
                  placeholder="Enter symbol (e.g., AAPL, BTC/USD)"
                />
              </div>
              <div>
                <label className="mb-1 block font-medium text-sm">Theme</label>
                <Select
                  value={currentTheme}
                  onChange={(theme) => setTvTheme(theme as "light" | "dark")}
                  options={[
                    { label: "Light", value: "light" },
                    { label: "Dark", value: "dark" },
                  ]}
                />
              </div>
              <div>
                <label className="mb-1 block font-medium text-sm">
                  Interval
                </label>
                <Select
                  value={currentInterval}
                  onChange={(interval) => setTvInterval(interval)}
                  options={[
                    { label: "1 Min", value: "1" },
                    { label: "15 Min", value: "15" },
                    { label: "30 Min", value: "30" },
                    { label: "1 Hour", value: "60" },
                    { label: "1 Day", value: "1D" },
                  ]}
                />
              </div>
              <div>
                <label className="mb-1 block font-medium text-sm">
                  Series Style
                </label>
                <Select
                  value={currentSeriesStyle}
                  onChange={(style) => setTvSeriesStyle(style)}
                  options={[
                    { label: "Bars", value: 0 },
                    { label: "Candles", value: 1 },
                    { label: "Line", value: 2 },
                    { label: "Area", value: 3 },
                    { label: "Heikin Ashi", value: 8 },
                    { label: "Hollow Candles", value: 9 },
                  ]}
                />
              </div>
              <div>
                <label className="mb-1 block font-medium text-sm">
                  Chart Height (px)
                </label>
                <Input
                  type="number"
                  value={currentHeight}
                  onChange={(e) => setTvHeight(Number(e.target.value))}
                  placeholder="Height in pixels"
                  min={200}
                  max={1200}
                />
              </div>
            </div>
          </div>

          {/* Chart Preview */}
          <div
            className="overflow-hidden rounded border border-gray-300"
            style={{ height: `${currentHeight}px` }}
          >
            <TVChartContainer
              key={JSON.stringify({
                currentSymbol,
                currentTheme,
                currentInterval,
                currentSeriesStyle,
                currentHeight,
                enabledFeatures,
                disabledFeatures,
              })}
              symbol={currentSymbol}
              interval={currentInterval}
              seriesStyle={currentSeriesStyle}
              crypto={currentSymbol.includes("/")}
              utcOffset={moment().format("Z")}
              exchange="NYSE"
              disabled_features={disabledFeatures}
              enabled_features={enabledFeatures}
              theme={currentTheme}
              baseUrl={window.env.DATA_API_URL}
              height={currentHeight}
            />
          </div>

          {/* Features Configuration */}
          <div className="rounded border border-gray-200 p-4">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-semibold text-base">
                Features Configuration
              </h3>
              <Button variant="secondary" onClick={resetTradingViewConfig}>
                Reset All
              </Button>
            </div>

            {/* Search */}
            <div className="mb-3">
              <Input
                value={featureSearchQuery}
                onChange={(e) => setFeatureSearchQuery(e.target.value)}
                placeholder="Search features..."
              />
            </div>

            {/* Stats */}
            <div className="mb-3 text-gray-600 text-sm">
              Showing {filteredFeatures.length} of {TRADINGVIEW_FEATURES.length}{" "}
              features
              {enabledFeatures.length > 0 &&
                ` • ${enabledFeatures.length} enabled`}
              {disabledFeatures.length > 0 &&
                ` • ${disabledFeatures.length} disabled`}
            </div>

            {/* Feature List */}
            <div className="max-h-96 overflow-y-auto rounded border border-gray-200">
              {filteredFeatures.map((feature) => {
                const state = getFeatureState(feature.name);
                return (
                  <div
                    key={feature.name}
                    className="flex cursor-pointer items-start gap-3 border-gray-100 border-b p-3 hover:bg-gray-50"
                    onClick={() => toggleFeature(feature.name)}
                  >
                    <div className="w-20 shrink-0">
                      <span
                        className={cn(
                          "inline-block rounded px-2 py-1 font-medium text-xs",
                          state === "enabled"
                            ? "bg-green-100 text-green-800"
                            : state === "disabled"
                              ? "bg-red-100 text-red-800"
                              : "bg-gray-100 text-gray-600",
                        )}
                      >
                        {state === "enabled"
                          ? "Enabled"
                          : state === "disabled"
                            ? "Disabled"
                            : "Default"}
                      </span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="break-all font-mono text-gray-900 text-sm">
                        {feature.name}
                      </div>
                      <div className="mt-1 text-gray-600 text-xs">
                        {feature.description}
                      </div>
                      <div className="mt-1 text-gray-400 text-xs">
                        Default: {feature.defaultValue ? "enabled" : "disabled"}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
